import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Download, AlertTriangle, Calculator, TrendingUp, TrendingDown, DollarSign, Info } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper — Singapore quarter ranges
const quarterRange = (year, q) => {
  const m0 = (q - 1) * 3;
  const start = new Date(year, m0, 1);
  const end = new Date(year, m0 + 3, 0);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: fmt(start), to: fmt(end) };
};

const fmtSGD = (n) => `$${(n || 0).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const GSTReport = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;

  const [year, setYear] = useState(String(currentYear));
  const [quarter, setQuarter] = useState(String(currentQ));
  const [gstInclusive, setGstInclusive] = useState(false);
  const [customRange, setCustomRange] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    let frm, to;
    if (customRange) {
      if (!dateFrom || !dateTo) {
        toast.error('Pick both From and To dates');
        return;
      }
      frm = dateFrom; to = dateTo;
    } else {
      const { from, to: t } = quarterRange(parseInt(year, 10), parseInt(quarter, 10));
      frm = from; to = t;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/gst/report`, {
        params: { date_from: frm, date_to: to, gst_inclusive: gstInclusive },
      });
      setData(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load GST report'));
    } finally {
      setLoading(false);
    }
  }, [year, quarter, gstInclusive, customRange, dateFrom, dateTo]);

  useEffect(() => { fetchReport(); /* initial */ }, [fetchReport]);

  const exportCSV = () => {
    if (!data) return;
    const b = data.boxes;
    const rows = [
      ['NJK Logistics — GST F5 Report'],
      ['Period', `${data.period.from} to ${data.period.to}`],
      ['GST Rate', `${(data.gst_rate * 100).toFixed(0)}%`],
      ['GST Inclusive in totals', data.gst_inclusive ? 'Yes' : 'No'],
      [],
      ['Box', 'Description', 'Amount (SGD)'],
      ['Box 1', 'Standard-rated supplies (sales excl. GST)', b.box1_standard_rated_supplies.toFixed(2)],
      ['Box 4', 'Total supplies', b.box4_total_supplies.toFixed(2)],
      ['Box 5', 'Taxable purchases (excl. GST)', b.box5_taxable_purchases.toFixed(2)],
      ['Box 6', 'Output tax (GST collected)', b.box6_output_tax.toFixed(2)],
      ['Box 7', 'Input tax (GST paid)', b.box7_input_tax.toFixed(2)],
      ['Box 8', 'Net GST payable / (refundable)', b.box8_net_gst_payable.toFixed(2)],
      [],
      ['Invoice Detail'],
      ['Date', 'Number', 'Source', 'Customer', 'Sales Total', 'Supplier Cost'],
      ...data.invoices.map(i => [i.date, i.number, i.source, i.customer, i.total.toFixed(2), i.supplier_cost.toFixed(2)]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GST_F5_${data.period.from}_to_${data.period.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

  const b = data?.boxes;

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin">
            <Button variant="outline" data-testid="back-btn"><ArrowLeft size={18} /></Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet size={28} className="text-indigo-600" /> GST F5 Report
            </h1>
            <p className="text-gray-600 text-sm mt-1">Singapore IRAS quarterly GST submission helper</p>
          </div>
        </div>

        {/* Banner: not yet registered */}
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Forecast mode — your company is not GST-registered yet.</p>
              <p className="text-xs mt-1">
                This report calculates <strong>what your GST would be</strong> at 9% based on your existing invoices and supplier costs.
                Use it to plan your registration and to test the numbers. When you register with IRAS, simply continue using this page each quarter.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <Label className="text-xs">Mode</Label>
                <Select value={customRange ? 'custom' : 'quarter'} onValueChange={(v) => setCustomRange(v === 'custom')}>
                  <SelectTrigger data-testid="mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarter">By Quarter</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!customRange ? (
                <>
                  <div>
                    <Label className="text-xs">Year</Label>
                    <Select value={year} onValueChange={setYear}>
                      <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Quarter</Label>
                    <Select value={quarter} onValueChange={setQuarter}>
                      <SelectTrigger data-testid="quarter-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Q1 (Jan – Mar)</SelectItem>
                        <SelectItem value="2">Q2 (Apr – Jun)</SelectItem>
                        <SelectItem value="3">Q3 (Jul – Sep)</SelectItem>
                        <SelectItem value="4">Q4 (Oct – Dec)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="from-date" />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="to-date" />
                  </div>
                </>
              )}

              <div className="flex items-center gap-2 pt-5">
                <Switch checked={gstInclusive} onCheckedChange={setGstInclusive} id="gst-inclusive" data-testid="gst-inclusive-toggle" />
                <Label htmlFor="gst-inclusive" className="text-xs cursor-pointer leading-tight">
                  Invoice totals already include GST
                </Label>
              </div>

              <div className="flex gap-2">
                <Button onClick={fetchReport} className="bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="generate-btn">
                  <Calculator size={16} className="mr-1" /> Recalculate
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
          </div>
        ) : data ? (
          <>
            {/* Box summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Box 1 — Sales</div>
                  <div className="text-xl font-bold text-gray-900 mt-1" data-testid="box1">{fmtSGD(b.box1_standard_rated_supplies)}</div>
                  <p className="text-[10px] text-gray-500 mt-1">Standard-rated supplies (excl. GST)</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Box 5 — Purchases</div>
                  <div className="text-xl font-bold text-gray-900 mt-1" data-testid="box5">{fmtSGD(b.box5_taxable_purchases)}</div>
                  <p className="text-[10px] text-gray-500 mt-1">Taxable purchases (excl. GST)</p>
                </CardContent>
              </Card>

              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
                    <TrendingUp size={11} /> Box 6 — Output Tax
                  </div>
                  <div className="text-xl font-bold text-emerald-700 mt-1" data-testid="box6">{fmtSGD(b.box6_output_tax)}</div>
                  <p className="text-[10px] text-emerald-700 mt-1">GST collected on sales</p>
                </CardContent>
              </Card>

              <Card className="border-rose-200 bg-rose-50/30">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold flex items-center gap-1">
                    <TrendingDown size={11} /> Box 7 — Input Tax
                  </div>
                  <div className="text-xl font-bold text-rose-700 mt-1" data-testid="box7">{fmtSGD(b.box7_input_tax)}</div>
                  <p className="text-[10px] text-rose-700 mt-1">GST paid on purchases</p>
                </CardContent>
              </Card>
            </div>

            {/* Net GST headline */}
            <Card className="mb-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
              <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-80">Box 8 — Net GST</div>
                  <div className="text-3xl font-extrabold mt-1" data-testid="box8">
                    {fmtSGD(Math.abs(b.box8_net_gst_payable))}
                  </div>
                  <p className="text-xs opacity-90 mt-1">
                    {b.box8_net_gst_payable >= 0 ? 'Payable to IRAS' : 'Refundable from IRAS'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={exportCSV} className="bg-white text-indigo-700 hover:bg-gray-100" data-testid="export-csv-btn">
                    <Download size={16} className="mr-2" /> Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Detail table */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h2 className="font-semibold text-gray-900">Invoice Detail</h2>
                  <div className="flex gap-3 text-xs text-gray-500">
                    <span>Vehicle: {data.totals.vehicle_invoice_count}</span>
                    <span>Warehouse: {data.totals.warehouse_invoice_count}</span>
                    <Badge variant="outline" className="text-[10px]">Gross Sales: {fmtSGD(data.totals.total_sales_gross)}</Badge>
                  </div>
                </div>
                {data.invoices.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 text-sm flex flex-col items-center gap-2">
                    <Info size={20} />
                    No invoices in this period.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="text-left p-3">Date</th>
                          <th className="text-left p-3">Number</th>
                          <th className="text-left p-3">Source</th>
                          <th className="text-left p-3">Customer</th>
                          <th className="text-right p-3">Sales</th>
                          <th className="text-right p-3">Supplier Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.invoices.map((i) => (
                          <tr key={`${i.source}-${i.id}`} className="border-t hover:bg-gray-50">
                            <td className="p-3 text-gray-600">{i.date}</td>
                            <td className="p-3 font-medium">{i.number}</td>
                            <td className="p-3">
                              <Badge variant="outline" className="text-[10px]">{i.source}</Badge>
                            </td>
                            <td className="p-3 truncate max-w-[200px]">{i.customer}</td>
                            <td className="p-3 text-right font-mono">{fmtSGD(i.total)}</td>
                            <td className="p-3 text-right font-mono text-gray-600">{fmtSGD(i.supplier_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* IRAS submission tip */}
            <Card className="mt-4 border-blue-200 bg-blue-50">
              <CardContent className="py-3 flex items-start gap-3 text-sm text-blue-900">
                <DollarSign size={18} className="text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Ready to submit?</p>
                  <p className="text-xs mt-1">
                    Once you are GST-registered, log in to{' '}
                    <a className="underline" target="_blank" rel="noreferrer" href="https://mytax.iras.gov.sg">myTax Portal (IRAS)</a>{' '}
                    each quarter, file the F5 form using the box values above, and pay Box 8 by the due date (1 month after the quarter ends).
                    Keep the CSV export as your filing record.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default GSTReport;
